import { Box, IconButton, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { SearchUsers } from 'components/core/search/users'
import { C1TableGrid } from 'components/core/table-grid/C1TableGrid'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { C1AppId, ServicePrincipalResourceTypeId } from 'lib/constants/system'
import { UserPlus, X } from 'lucide-react'
import {
    AddAppResourceOwnerRequest,
    RemoveAppResourceOwnerRequest,
    SearchAppResourceOwnersRequest,
} from 'pbts/c1/api/app/v1/app_resource_owner'
import type { User } from 'pbts/c1/api/user/v1/user'
import { UserStatus } from 'pbts/c1/api/user/v1/user'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
    addAppResourceOwnersApi,
    removeAppResourceOwnersApi,
    searchAppResourceOwnersApi,
} from 'redux_toolkit/app-resources'

type SPOwnersTableProps = {
    userId: string
    isEditable: boolean
}

export const SPOwnersTable: FC<SPOwnersTableProps> = ({ userId, isEditable }) => {
    const intl = useIntl()
    const [showAddOwner, setShowAddOwner] = useState(false)

    const { data: owners, isLoading: ownersLoading } = searchAppResourceOwnersApi.useSearch(
        SearchAppResourceOwnersRequest.create({
            appId: C1AppId,
            appResourceId: userId,
        }),
        { notify: true, autoReload: true },
    )

    const { mutate: addOwner } = addAppResourceOwnersApi()
    const { mutate: removeOwner } = removeAppResourceOwnersApi()

    const currentOwners = useMemo(() => owners?.flat() ?? [], [owners])

    const handleAddOwners = useCallback(async (newOwners: User[]): Promise<void> => {
        const currentIds = new Set(currentOwners.map((u) => u?.id).filter(Boolean))
        for (const u of newOwners) {
            if (!currentIds.has(u.id)) {
                await addOwner(AddAppResourceOwnerRequest.create({
                    appId: C1AppId,
                    resourceId: userId,
                    resourceTypeId: ServicePrincipalResourceTypeId,
                    userId: u.id,
                }))
            }
        }
        setShowAddOwner(false)
    }, [currentOwners, userId, addOwner])

    const handleRemoveOwner = useCallback(async (ownerId: string): Promise<void> => {
        await removeOwner(RemoveAppResourceOwnerRequest.create({
            appId: C1AppId,
            resourceId: userId,
            resourceTypeId: ServicePrincipalResourceTypeId,
            userId: ownerId,
        }))
    }, [userId, removeOwner])

    const addOwnerButton = isEditable
        ? <C1Button
            variant="outlined"
            onClick={(): void => setShowAddOwner(!showAddOwner)}
            startIcon={<UserPlus size={16} />}
        >
            <FormattedMessage id="sp_owners_add" defaultMessage="Add owner" />
        </C1Button>
        : undefined

    return (
        <C1TableGrid
            title={<FormattedMessage id="sp_owners_title" defaultMessage="Owners" />}
            buttons={addOwnerButton}
        >
            {showAddOwner && isEditable && (
                <Box sx={{ pb: 2 }}>
                    <SearchUsers
                        users={currentOwners.filter((u): u is User => !!u)}
                        onSelected={handleAddOwners}
                        baseRequest={{ userStatuses: [UserStatus.ENABLED] }}
                        label={false}
                    />
                </Box>
            )}
            {ownersLoading ? (
                <Stack spacing={1}>
                    <Skeleton height={48} />
                    <Skeleton height={48} />
                </Stack>
            ) : currentOwners.length > 0 ? (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Typography variant="subtitle2" color="text.secondary">
                                    <FormattedMessage id="sp_owners_col_name" defaultMessage="Name" />
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="subtitle2" color="text.secondary">
                                    <FormattedMessage id="sp_owners_col_email" defaultMessage="Email" />
                                </Typography>
                            </TableCell>
                            {isEditable && (
                                <TableCell width={48} />
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {currentOwners.filter((u): u is User => !!u).map((owner) => (
                            <TableRow key={owner.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                <TableCell>
                                    <UserDisplayName
                                        user={owner}
                                        readOnly
                                        avatarSize="sm"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        {owner.email}
                                    </Typography>
                                </TableCell>
                                {isEditable && (
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            aria-label={intl.formatMessage(
                                                {
                                                    id: 'sp_owners_remove',
                                                    defaultMessage: 'Remove owner {name}',
                                                },
                                                { name: owner.displayName || owner.email || owner.id },
                                            )}
                                            onClick={(): void => { void handleRemoveOwner(owner.id) }}
                                        >
                                            <X size={16} />
                                        </IconButton>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary" sx={{ mb: 1 }}>
                        <FormattedMessage
                            id="sp_owners_empty"
                            defaultMessage="No owners assigned. Add an owner to delegate management of this service principal."
                        />
                    </Typography>
                    {isEditable && (
                        <C1Button
                            variant="outlined"
                            onClick={(): void => setShowAddOwner(true)}
                            startIcon={<UserPlus size={16} />}
                        >
                            <FormattedMessage id="sp_owners_add" defaultMessage="Add owner" />
                        </C1Button>
                    )}
                </Box>
            )}
        </C1TableGrid>
    )
}
